import {
  AccessTime,
  AppRegistration,
  Event,
  Fingerprint,
  Image,
  Layers,
  ScreenshotMonitor,
  Sell,
} from '@mui/icons-material';
import { Button, CardMedia } from '@mui/material';
import { Box } from '@mui/system';
import type { FormikProps } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  DetailAccordion,
  DetailAccordionContainer,
  DetailAccordionDetails,
  DetailAccordionSummary,
} from '~/components/DetailAccordion';
import { DetailSection } from '~/components/DetailSection';
import { TagList } from '~/components/tag';
import { formatDate, formatInterval } from '~/lib/datetime';
import { toDataUri } from '~/lib/file';
import type { EditorUserApp, FormValues } from '../lib';

interface AppFormDetailsProps {
  formik: FormikProps<FormValues>;
  userApp: EditorUserApp | null | undefined;
}

export const AppFormDetails = ({ formik, userApp }: AppFormDetailsProps) => {
  const { t } = useTranslation();
  const { getInputProps, open: selectFile } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      if (accepted.length !== 1 || !userApp?.id) return;
      const thumbnailUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('thumbnailUri', thumbnailUri, true);
    },
  });

  if (!userApp) return null;

  return (
    <DetailAccordionContainer>
      <DetailAccordion defaultExpanded>
        <DetailAccordionSummary>{t('common.labels.info')}</DetailAccordionSummary>
        <DetailAccordionDetails>
          <DetailSection icon="Aa" title={t('common.labels.name')} value={userApp.name} />

          {userApp.id && (
            <DetailSection
              icon={<Fingerprint />}
              title={t('common.labels.id')}
              value={userApp.id.toString()}
            />
          )}

          {userApp.overlay && (
            <DetailSection
              icon={<Layers />}
              title={t('common.form_labels.overlay_id')}
              value={userApp.overlay.id.toString()}
            />
          )}

          <DetailSection
            icon={<AppRegistration />}
            title={t('common.labels.template')}
            value={`${userApp.template.name} ${userApp.templateVersion.version}`}
          />

          <DetailSection
            icon={<AccessTime />}
            title={t('common.form_labels.default_duration')}
            value={`${
              userApp.templateVersion.defaultDuration
                ? formatInterval(userApp.templateVersion.defaultDuration)
                : userApp.defaultDuration
                ? formatInterval(userApp.defaultDuration)
                : t('common.labels.n_a')
            }`}
          />

          {userApp.createdAt && (
            <DetailSection
              icon={<Event />}
              title={t('common.labels.created')}
              value={formatDate(userApp.createdAt)}
            />
          )}

          {userApp.updatedAt && (
            <DetailSection
              icon={<Event />}
              title={t('common.labels.updated')}
              value={formatDate(userApp.updatedAt)}
            />
          )}
        </DetailAccordionDetails>
      </DetailAccordion>

      <DetailAccordion defaultExpanded>
        <DetailAccordionSummary>{t('common.labels.misc')}</DetailAccordionSummary>
        <DetailAccordionDetails>
          {userApp.id && (
            <>
              <DetailSection
                icon={<ScreenshotMonitor />}
                title={t('common.labels.thumbnail')}
                value={
                  <Box sx={{ maxWidth: '200px' }}>
                    {formik.values.thumbnailUri || userApp.thumbnailUri ? (
                      <CardMedia
                        alt={t('common.labels.current_thumbnail')}
                        image={formik.values.thumbnailUri || userApp.thumbnailUri || undefined}
                        crossOrigin="anonymous"
                        component="img"
                      />
                    ) : (
                      t('common.fallbacks.no_thumbnail')
                    )}
                    <input {...getInputProps()} />
                  </Box>
                }
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  color="primary"
                  onClick={selectFile}
                  startIcon={<Image />}
                  variant="contained"
                  size="small"
                >
                  {t('common.dialogs.update')}
                </Button>
              </Box>
            </>
          )}
          <DetailSection
            icon={<Sell />}
            title={t('common.labels.tags')}
            value={
              userApp.id && userApp.tags && userApp.tags.length > 0 ? (
                <TagList item={{ __typename: 'ContentItem', id: userApp.id }} tags={userApp.tags} />
              ) : (
                '--'
              )
            }
          />
        </DetailAccordionDetails>
      </DetailAccordion>
    </DetailAccordionContainer>
  );
};
